import { useCallback, useEffect, useRef, type ComponentRef, type MutableRefObject } from 'react';
import dayjs from 'dayjs';
import { Controller, type Control, type FieldErrors, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import {
  AppButton,
  AppCheckbox,
  AppForm,
  AppFormLabel,
  AppRadio,
  AppRadioGroup,
  AppTextField,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import useAppModal from '@/hooks/useAppModal';
import type { UseJapaneseNameKanaFieldsResult } from '@/hooks/useJapaneseNameKanaFields';
import { halfWidthToFullWidthAlphaNum as toFullWidthAlphanumeric, normalizeAddress } from '@/utils';
import { dateValueToIso } from '@/utils/dateConverter';
import {
  PATIENT_ADDRESS1_MAX_LENGTH,
  PATIENT_ADDRESS2_MAX_LENGTH,
  PATIENT_ADDRESS3_MAX_LENGTH,
  PATIENT_ADDRESS_KANA_MAX_LENGTH,
  PATIENT_ADDRESS_NAME_MAX_LENGTH,
  PATIENT_TELEPHONE_MAX_LENGTH,
  type PatientFormSchemaType,
} from '@pages/main/patient/types';
import {
  ADDRESS3_CALC_TARGET_CONFIRM_HINT,
  ADDRESS3_CALC_TARGET_CONFIRM_QUESTION,
  SPECIAL_AREA_ADDITION_WARNING,
} from '@pages/main/patient/constants/validationMessages';
import {
  normalizePostalDigits as normalizePatientPostalDigits,
  normalizePatientTelephone,
} from './PatientSectionShared.helpers';

type AppTextFieldRef = ComponentRef<typeof AppTextField>;
type BasicOption = { id: number; name: string };

interface PatientInfoSectionFormProps {
  control: Control<PatientFormSchemaType>;
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  errors: FieldErrors<PatientFormSchemaType>;
  disabled: boolean;
  age: number | null;
  sexOptions: BasicOption[];
  patientNameKanaMaxLength: number;
  patientNameInputProps: UseJapaneseNameKanaFieldsResult['nameInputProps'];
  patientNameKanaInputProps: UseJapaneseNameKanaFieldsResult['kanaInputProps'];
  onAddressSearch: () => void;
  onOpenMap: () => void;
  onPatientNameBlur: () => void;
  address2Ref?: MutableRefObject<{ focus?: () => void } | null>;
}

export function PatientInfoSectionForm({
  control,
  watch,
  setValue,
  errors,
  disabled,
  age,
  sexOptions,
  patientNameKanaMaxLength,
  patientNameInputProps,
  patientNameKanaInputProps,
  onAddressSearch,
  onOpenMap,
  onPatientNameBlur,
  address2Ref,
}: PatientInfoSectionFormProps) {
  const { confirm } = useAppModal();
  const patientPostalCode2Ref = useRef<AppTextFieldRef | null>(null);
  const prevPatientPc1DigitsLenRef = useRef(0);
  const patientPostalCode1Value = watch('patient_information.postal_code1');

  useEffect(() => {
    prevPatientPc1DigitsLenRef.current = normalizePatientPostalDigits(
      patientPostalCode1Value || ''
    ).length;
  }, [patientPostalCode1Value]);

  const syncAddressToInsurance = useCallback(
    (next: Partial<{
      address1: string;
      address2: string;
      address3: string;
      addressName: string;
    }>) => {
      const address1 =
        next.address1 ?? String(watch('patient_information.address1') ?? '');
      const address2 =
        next.address2 ?? String(watch('patient_information.address2') ?? '');
      const address3 =
        next.address3 ?? String(watch('patient_information.address3') ?? '');
      const addressName =
        next.addressName ?? String(watch('patient_information.address_name') ?? '');
      const insuredAddress2 = [address2, address3, addressName]
        .map(value => value.trim())
        .filter(Boolean)
        .join(' ')
        .slice(0, PATIENT_ADDRESS2_MAX_LENGTH);

      setValue('health_insurance_card.address1', address1.slice(0, PATIENT_ADDRESS1_MAX_LENGTH), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setValue('health_insurance_card.address2', insuredAddress2, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    },
    [setValue, watch]
  );

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6">
        <AppFormLabel label="氏名漢字" required />
        <div>
          <Controller
            name="patient_information.name"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <div className="flex flex-wrap items-start gap-2">
                  <AppTextField
                    {...field}
                    placeholder="氏名漢字"
                    maxLength={patientNameKanaMaxLength}
                    className="min-w-0 flex-1"
                    status={fieldState.invalid ? 'error' : undefined}
                    {...patientNameInputProps}
                    disabled={disabled}
                    onBlur={e => {
                      patientNameInputProps.onBlur?.(e);
                      field.onBlur();
                      onPatientNameBlur();
                    }}
                  />
                  <AppCheckbox
                    checked={watch('patient_information.gaiji_check')}
                    disabled={disabled}
                    onChange={e => setValue('patient_information.gaiji_check', e.target.checked)}
                  >
                    <span className="text-sm whitespace-nowrap">外字</span>
                  </AppCheckbox>
                </div>
              </AppForm.Item>
            )}
          />
        </div>

        <AppFormLabel label="氏名カナ" required />
        <div>
          <Controller
            name="patient_information.name_kana"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppTextField
                  {...field}
                  value={field.value ?? ''}
                  placeholder="氏名カタカナ"
                  maxLength={patientNameKanaMaxLength}
                  status={fieldState.invalid ? 'error' : undefined}
                  {...patientNameKanaInputProps}
                  disabled={disabled}
                  onBlur={e => {
                    patientNameKanaInputProps.onBlur?.(e);
                    field.onBlur();
                    onPatientNameBlur();
                  }}
                />
              </AppForm.Item>
            )}
          />
        </div>

        <AppFormLabel label="性別" required />
        <div>
          <Controller
            name="patient_information.sex_id"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppRadioGroup
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  className="items-start"
                  disabled={disabled}
                >
                  {sexOptions.map(sex => (
                    <AppRadio key={sex.id} value={sex.id}>
                      {sex.name}
                    </AppRadio>
                  ))}
                </AppRadioGroup>
              </AppForm.Item>
            )}
          />
        </div>

        <AppFormLabel label="生年月日" required />
        <div>
          <Controller
            name="patient_information.date_of_birth"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                    className="w-40"
                    placeholder="元号00年00月00日"
                    endDate={dayjs().format('YYYY-MM-DD')}
                    error={fieldState.invalid}
                    disabled={disabled}
                  />
                  <AppTextField
                    value={age !== null ? String(age) : ''}
                    disabled
                    className="w-16 bg-gray-100"
                  />
                  <span className="text-sm">歳</span>
                </div>
              </AppForm.Item>
            )}
          />
        </div>
      </div>

      <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6 xl:border-l xl:pl-6">
        <AppFormLabel label="郵便番号" />
        <div className="space-y-1">
          <div className="grid grid-cols-[minmax(0,4.75rem)_minmax(0,4.75rem)_auto] items-center gap-x-2 gap-y-1">
            <Controller
              name="patient_information.postal_code1"
              control={control}
              render={({ field, fieldState }) => (
                <AppTextField
                  {...field}
                  placeholder="000"
                  inputMode="numeric"
                  className="min-w-0 w-full"
                  status={fieldState.invalid ? 'error' : undefined}
                  disabled={disabled}
                  onChange={e => {
                    const digits = normalizePatientPostalDigits((e.target as HTMLInputElement).value);
                    const prevLen = prevPatientPc1DigitsLenRef.current;
                    const pc1 = digits.slice(0, 3);
                    const overflow = digits.slice(3, 7);
                    prevPatientPc1DigitsLenRef.current = pc1.length;
                    field.onChange(pc1);
                    if (overflow.length > 0) {
                      setValue('patient_information.postal_code2', overflow.slice(0, 4), {
                        shouldDirty: true,
                        shouldValidate: false,
                      });
                      queueMicrotask(() => patientPostalCode2Ref.current?.focus?.());
                    } else if (pc1.length === 3 && digits.length > prevLen) {
                      queueMicrotask(() => patientPostalCode2Ref.current?.focus?.());
                    }
                  }}
                  onPaste={e => {
                    if (disabled) return;
                    const clip = normalizePatientPostalDigits(e.clipboardData.getData('text') || '');
                    if (clip.length < 2) return;
                    e.preventDefault();
                    const input = e.currentTarget;
                    const current = normalizePatientPostalDigits(input.value || '');
                    const selStart = input.selectionStart ?? current.length;
                    const selEnd = input.selectionEnd ?? current.length;
                    const merged =
                      `${current.slice(0, selStart)}${clip}${current.slice(selEnd)}`.replace(
                        /\D/g,
                        ''
                      );
                    const pc1 = merged.slice(0, 3);
                    const overflow = merged.slice(3, 7);
                    prevPatientPc1DigitsLenRef.current = pc1.length;
                    field.onChange(pc1);
                    if (overflow.length > 0) {
                      setValue('patient_information.postal_code2', overflow.slice(0, 4), {
                        shouldDirty: true,
                        shouldValidate: false,
                      });
                    }
                    queueMicrotask(() => patientPostalCode2Ref.current?.focus?.());
                  }}
                  onBlur={e => {
                    const normalized = normalizePatientPostalDigits(
                      (e.target as HTMLInputElement).value || ''
                    ).slice(0, 3);
                    setValue('patient_information.postal_code1', normalized, {
                      shouldValidate: false,
                    });
                    prevPatientPc1DigitsLenRef.current = normalized.length;
                    field.onBlur();
                  }}
                />
              )}
            />
            <Controller
              name="patient_information.postal_code2"
              control={control}
              render={({ field, fieldState: { invalid } }) => {
                const { ref: rhfRef, ...fieldProps } = field;
                return (
                  <AppTextField
                    {...fieldProps}
                    ref={instance => {
                      patientPostalCode2Ref.current = instance;
                      rhfRef(instance);
                    }}
                    placeholder="0000"
                    maxLength={4}
                    inputMode="numeric"
                    className="min-w-0 w-full"
                    status={invalid ? 'error' : undefined}
                    disabled={disabled}
                    onChange={e => {
                      const normalized = normalizePatientPostalDigits(
                        (e.target as HTMLInputElement).value
                      ).slice(0, 4);
                      field.onChange(normalized);
                    }}
                    onBlur={e => {
                      const normalized = normalizePatientPostalDigits(
                        (e.target as HTMLInputElement).value || ''
                      ).slice(0, 4);
                      setValue('patient_information.postal_code2', normalized, {
                        shouldValidate: false,
                      });
                      field.onBlur();
                    }}
                  />
                );
              }}
            />
            <AppButton
              type="primary"
              className="justify-self-start"
              onClick={onAddressSearch}
              disabled={disabled}
            >
              住所補助
            </AppButton>
            {(errors.patient_information?.postal_code1?.message ||
              errors.patient_information?.postal_code2?.message) && (
              <>
                <div className="min-w-0 self-start text-sm leading-tight text-[var(--ant-color-error)] break-words">
                  {errors.patient_information?.postal_code1?.message}
                </div>
                <div className="min-w-0 self-start text-sm leading-tight text-[var(--ant-color-error)] break-words">
                  {errors.patient_information?.postal_code2?.message}
                </div>
                <div className="min-w-0" aria-hidden />
              </>
            )}
          </div>
        </div>

        <AppFormLabel label="住所" required />
        <div className="space-y-1">
          <div className="space-y-0.5">
            <div className="flex items-start gap-2">
              <Controller
                name="patient_information.address1"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    className="mb-0 flex-1 min-w-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...field}
                      maxLength={PATIENT_ADDRESS1_MAX_LENGTH}
                      className="flex-1"
                      status={fieldState.invalid ? 'error' : undefined}
                      disabled={disabled}
                      onChange={e => {
                        const next = e.target.value.slice(0, PATIENT_ADDRESS1_MAX_LENGTH);
                        field.onChange(next);
                        syncAddressToInsurance({ address1: next });
                      }}
                      onBlur={e => {
                        const val = (e.target as HTMLInputElement).value || '';
                        const normalized = normalizeAddress(val).slice(
                          0,
                          PATIENT_ADDRESS1_MAX_LENGTH
                        );
                        if (normalized !== val) {
                          setValue('patient_information.address1', normalized, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: false,
                          });
                          syncAddressToInsurance({ address1: normalized });
                        }
                        field.onBlur();
                      }}
                    />
                  </AppForm.Item>
                )}
              />
              <AppButton
                type="primary"
                className="shrink-0 self-start"
                onClick={onOpenMap}
                data-skip-enter-nav
                disabled={disabled}
              >
                地図
              </AppButton>
            </div>
          </div>
          <div className="space-y-0.5">
            <Controller
              name="patient_information.address2"
              control={control}
              render={({ field, fieldState }) => {
                const { ref: rhfRef, ...fieldProps } = field;
                return (
                  <AppForm.Item
                    className="mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...fieldProps}
                      ref={instance => {
                        if (address2Ref) address2Ref.current = instance;
                        rhfRef(instance);
                      }}
                      maxLength={PATIENT_ADDRESS2_MAX_LENGTH}
                      status={fieldState.invalid ? 'error' : undefined}
                      disabled={disabled}
                      onChange={e => {
                        const next = e.target.value.slice(0, PATIENT_ADDRESS2_MAX_LENGTH);
                        field.onChange(next);
                        syncAddressToInsurance({ address2: next });
                      }}
                      onBlur={e => {
                        const val = (e.target as HTMLInputElement).value || '';
                        const normalized = normalizeAddress(val).slice(
                          0,
                          PATIENT_ADDRESS2_MAX_LENGTH
                        );
                        if (normalized !== val) {
                          setValue('patient_information.address2', normalized, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: false,
                          });
                          syncAddressToInsurance({ address2: normalized });
                        }
                        field.onBlur();
                      }}
                    />
                  </AppForm.Item>
                );
              }}
            />
          </div>
        </div>

        <AppFormLabel label="建物・部屋" />
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1">
              <Controller
                name="patient_information.address3"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    className="mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...field}
                      maxLength={PATIENT_ADDRESS3_MAX_LENGTH}
                      status={fieldState.invalid ? 'error' : undefined}
                      disabled={disabled}
                      onChange={e => {
                        const next = e.target.value.slice(0, PATIENT_ADDRESS3_MAX_LENGTH);
                        field.onChange(next);
                        syncAddressToInsurance({ address3: next });
                      }}
                      onBlur={e => {
                        const val = (e.target as HTMLInputElement).value || '';
                        const normalized = normalizeAddress(val);
                        const converted = toFullWidthAlphanumeric(normalized).slice(
                          0,
                          PATIENT_ADDRESS3_MAX_LENGTH
                        );
                        if (converted !== val) {
                          setValue('patient_information.address3', converted, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: false,
                          });
                          syncAddressToInsurance({ address3: converted });
                        }
                        field.onBlur();
                      }}
                    />
                  </AppForm.Item>
                )}
              />
            </div>
            <AppFormLabel label="名称" />
            <div className="min-w-[8rem] flex-1 sm:flex-none">
              <Controller
                name="patient_information.address_name"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    className="mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...field}
                      maxLength={PATIENT_ADDRESS_NAME_MAX_LENGTH}
                      status={fieldState.invalid ? 'error' : undefined}
                      disabled={disabled}
                      onChange={e => {
                        const next = e.target.value.slice(0, PATIENT_ADDRESS_NAME_MAX_LENGTH);
                        field.onChange(next);
                        syncAddressToInsurance({ addressName: next });
                      }}
                      onBlur={e => {
                        const val = (e.target as HTMLInputElement).value || '';
                        const normalized = normalizeAddress(val);
                        const converted = toFullWidthAlphanumeric(normalized).slice(
                          0,
                          PATIENT_ADDRESS_NAME_MAX_LENGTH
                        );
                        if (converted !== val) {
                          setValue('patient_information.address_name', converted, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: false,
                          });
                          syncAddressToInsurance({ addressName: converted });
                        }
                        field.onBlur();
                      }}
                    />
                  </AppForm.Item>
                )}
              />
            </div>
            <div className="flex shrink-0 flex-nowrap items-center gap-6">
              <AppCheckbox
                checked={watch('patient_information.is_facility_resident')}
                disabled={disabled}
                onChange={e => {
                  setValue('patient_information.is_facility_resident', e.target.checked, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: false,
                  });
                }}
              >
                施設入居
              </AppCheckbox>
              <AppCheckbox
                checked={watch('patient_information.is_special_area_addition')}
                disabled={disabled}
                onChange={e => {
                  if (e.target.checked) {
                    confirm({
                      title: SPECIAL_AREA_ADDITION_WARNING,
                      okText: 'はい',
                      cancelText: 'いいえ',
                      onOk: () => {
                        setValue('patient_information.is_special_area_addition', true, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: false,
                        });
                      },
                      onCancel: () => {
                        setValue('patient_information.is_special_area_addition', false, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: false,
                        });
                      },
                    });
                  } else {
                    setValue('patient_information.is_special_area_addition', false, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: false,
                    });
                  }
                }}
              >
                特別地域加算
              </AppCheckbox>
              <AppCheckbox
                checked={watch('patient_information.address3_check')}
                disabled={disabled}
                onChange={e => {
                  if (e.target.checked) {
                    confirm({
                      title: (
                        <>
                          {ADDRESS3_CALC_TARGET_CONFIRM_QUESTION}
                          <br />
                          {ADDRESS3_CALC_TARGET_CONFIRM_HINT}
                        </>
                      ),
                      cancelText: 'キャンセル',
                      onOk: () => {
                        setValue('patient_information.address3_check', true);
                      },
                      onCancel: () => {
                        setValue('patient_information.address3_check', false);
                      },
                    });
                  } else {
                    setValue('patient_information.address3_check', false);
                  }
                }}
              >
                算定対象
              </AppCheckbox>
            </div>
          </div>
        </div>

        <AppFormLabel label="電話番号" />
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 gap-y-1">
          <div className="min-w-0">
            <Controller
              name="patient_information.telephone"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0 min-w-0"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <AppTextField
                    {...field}
                    maxLength={PATIENT_TELEPHONE_MAX_LENGTH}
                    className="w-full min-w-0"
                    status={fieldState.invalid ? 'error' : undefined}
                    disabled={disabled}
                    onChange={e => {
                      const filtered = (e.target as HTMLInputElement).value
                        .replace(/[^0-9０-９\-－ー―ｰ]/g, '')
                        .slice(0, PATIENT_TELEPHONE_MAX_LENGTH);
                      field.onChange(filtered);
                    }}
                    onBlur={e => {
                      const val = (e.target as HTMLInputElement).value || '';
                      const clamped = normalizePatientTelephone(val);
                      if (clamped !== val) {
                        setValue('patient_information.telephone', clamped, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: false,
                        });
                      }
                      field.onBlur();
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
          <AppFormLabel label="住所カナ" className="px-1" />
          <div className="min-w-0">
            <Controller
              name="patient_information.address_kana"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0 min-w-0"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    maxLength={PATIENT_ADDRESS_KANA_MAX_LENGTH}
                    className="w-full min-w-0"
                    status={fieldState.invalid ? 'error' : undefined}
                    disabled={disabled}
                    onChange={e => {
                      const next = (e.target.value || '').slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH);
                      field.onChange(next);
                    }}
                    onBlur={e => {
                      const val = (e.target as HTMLInputElement).value || '';
                      const clamped = val.slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH);
                      if (clamped !== val) {
                        setValue('patient_information.address_kana', clamped, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                      }
                      field.onBlur();
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
